/**
 * Réconciliation « fiche d'origine » (source_object) ↔ contexte / demandeur d'une NC.
 *
 * QUAL-001 §5.5 — Quand une non-conformité est signalée DEPUIS une fiche (client,
 * apprenant, formation…), la source ne doit pas rester une simple bannière : elle
 * pré-remplit le bon slot du contexte (parties prenantes) et peut suggérer le
 * demandeur. Ces mappings sont centralisés ici pour que TOUT point d'entrée
 * « Signaler une NC » en bénéficie sans dupliquer la logique.
 */

import type { NCContext, SourceObjectType } from '@/hooks/useQuality.types'

/** Slot de contexte (id + nom/référence) alimenté par un type de source. */
interface ContextSlotFields {
    id: keyof NCContext
    name?: keyof NCContext
}

/** Type de source → slot de contexte. Seuls les types ayant un emplacement
 *  visible (picker) ou lecture seule dans l'onglet Contexte sont mappés ;
 *  les autres restent tracés via la seule puce de provenance. */
const SOURCE_TO_CONTEXT: Partial<Record<SourceObjectType, ContextSlotFields>> = {
    client: { id: 'client_id', name: 'client_name' },
    learner: { id: 'learner_id', name: 'learner_name' },
    instructor: { id: 'instructor_id', name: 'instructor_name' },
    // Le slot `product_*` du contexte est celui de la FORMATION (picker de type
    // `formation`, lien vers `/catalog/formations/:id`). Un ARTICLE n'y a pas sa
    // place : sous l'ancien type commun `product`, signaler une NC depuis un
    // article pré-remplissait le slot Formation avec un id d'article, et le
    // « Voir la fiche » du contexte menait à une page introuvable. L'article
    // n'a pas de slot de contexte : sa traçabilité passe par `source_object_*`.
    formation: { id: 'product_id', name: 'product_name' },
    location: { id: 'location_id', name: 'location_name' },
    session: { id: 'session_id', name: 'session_name' },
    quote: { id: 'quote_id', name: 'quote_reference' },
    invoice: { id: 'invoice_id', name: 'invoice_reference' },
}

/**
 * Pré-remplit le contexte JSONB depuis la fiche d'origine d'un signalement.
 * Renvoie `null` si le type de source n'a pas de slot de contexte (la
 * traçabilité reste alors portée par `source_object_*`).
 */
export function contextHintsFromSource(
    source: { type: SourceObjectType; id: string; label: string } | null | undefined,
): NCContext | null {
    if (!source) return null
    const slot = SOURCE_TO_CONTEXT[source.type]
    if (!slot) return null
    const ctx = {} as Record<string, string | null>
    ctx[slot.id] = source.id
    if (slot.name) ctx[slot.name] = source.label
    return ctx as NCContext
}

/** Type de source → code catégorie de demandeur (référentiel REQUESTER_CATEGORY). */
const SOURCE_TO_REQUESTER_CATEGORY: Partial<Record<SourceObjectType, string>> = {
    client: 'client',
    learner: 'apprenant',
    instructor: 'intervenant',
}

/**
 * Code catégorie de demandeur déductible d'un type de source (client/apprenant/
 * intervenant), pour la suggestion « Utiliser … comme demandeur ». `null` sinon.
 */
export function requesterCategoryFromSource(type: string | null | undefined): string | null {
    if (!type) return null
    return SOURCE_TO_REQUESTER_CATEGORY[type as SourceObjectType] ?? null
}

/**
 * Code catégorie de demandeur → slot de contexte à **pré-remplir** quand le
 * demandeur est une entité (le demandeur est aussi une partie prenante). Limité
 * aux catégories dont le rôle est non ambigu ; `financeur` (= client mais ≠ client
 * formé), `collaborateur` (user, pas de slot) et `organisme_externe` sont exclus.
 */
const REQUESTER_CATEGORY_TO_CONTEXT: Record<string, Required<ContextSlotFields>> = {
    client: { id: 'client_id', name: 'client_name' },
    apprenant: { id: 'learner_id', name: 'learner_name' },
    intervenant: { id: 'instructor_id', name: 'instructor_name' },
}

/** Slot de contexte à pré-remplir depuis une catégorie de demandeur (`null` si non applicable). */
export function requesterContextSlot(
    categoryCode: string | null | undefined,
): Required<ContextSlotFields> | null {
    if (!categoryCode) return null
    return REQUESTER_CATEGORY_TO_CONTEXT[categoryCode] ?? null
}
